import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from 'components/AppIcon';

const LandingPage = () => {
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const testimonials = [
    {
      name: 'Marcus Reid',
      role: 'Online PT · 34 clients',
      quote: 'I used to manage workouts in Google Sheets, nutrition in MyFitnessPal, and messages in WhatsApp. FormForge replaced all three. My clients actually follow their plans now.',
      metric: '34 clients',
      metricLabel: 'managed in one place',
      avatar: 'MR',
      color: '#FF6B00',
    },
    {
      name: 'Priya Sharma',
      role: 'Strength Coach · 18 clients',
      quote: 'The progress tracking alone is worth it. Before/after photos, body metrics, and adherence charts — my clients can see their own transformation and it keeps them motivated.',
      metric: '92%',
      metricLabel: 'avg. client adherence',
      avatar: 'PS',
      color: '#00D4FF',
    },
    {
      name: 'James Okafor',
      role: 'Hybrid Trainer · 22 clients',
      quote: 'Onboarding used to take me 30 minutes per client. Now it\'s under 5. The intake questionnaire and assessment tools are exactly what I needed.',
      metric: '5 min',
      metricLabel: 'to onboard a new client',
      avatar: 'JO',
      color: '#AAFF00',
    },
  ];

  const steps = [
    {
      number: '01',
      title: 'Onboard your client',
      desc: 'Send an intake link. Clients complete their health history, fitness baseline, and goals — all captured in FormForge before their first session.',
      icon: 'UserPlus',
      color: '#FF6B00',
    },
    {
      number: '02',
      title: 'Build their programme',
      desc: 'Create a custom workout plan and nutrition programme from your library. Assign it in one click. Clients see it immediately in their portal.',
      icon: 'Layers',
      color: '#00D4FF',
    },
    {
      number: '03',
      title: 'Track and adjust',
      desc: 'Monitor adherence, review progress photos, and message clients — all from one dashboard. Adjust plans on the fly when life happens.',
      icon: 'TrendingUp',
      color: '#AAFF00',
    },
  ];

  const features = [
    {
      icon: 'Dumbbell',
      title: 'Workout Builder',
      desc: 'Drag-and-drop workout creation with a full exercise library. Build once, assign to multiple clients.',
      accent: '#FF6B00',
      size: 'lg:col-span-2',
      highlight: true,
    },
    {
      icon: 'Apple',
      title: 'Nutrition Planning',
      desc: 'Macro-balanced meal plans from a curated food library. Track calories, protein, carbs, and fat per client.',
      accent: '#00D4FF',
      size: 'lg:col-span-1',
    },
    {
      icon: 'Camera',
      title: 'Progress Tracking',
      desc: 'Photo comparisons, body metrics, and visual charts. Clients see how far they\'ve come.',
      accent: '#AAFF00',
      size: 'lg:col-span-1',
    },
    {
      icon: 'MessageCircle',
      title: 'Client Messaging',
      desc: 'Built-in messaging so everything stays in one place. No more scattered DMs.',
      accent: '#FF6B00',
      size: 'lg:col-span-1',
    },
    {
      icon: 'BarChart2',
      title: 'Performance Analytics',
      desc: 'Adherence rates, completion streaks, and AI coaching insights to help you coach smarter.',
      accent: '#00D4FF',
      size: 'lg:col-span-2',
      highlight: true,
    },
  ];

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ background: '#0A0A0B', color: '#E8EAED' }}>
      {/* ── NAV ── */}
      <nav
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
        style={{
          background: scrollY > 40 ? 'rgba(10,10,11,0.94)' : 'transparent',
          backdropFilter: scrollY > 40 ? 'blur(20px)' : 'none',
          borderBottom: scrollY > 40 ? '1px solid rgba(255,255,255,0.06)' : 'none',
        }}
      >
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #FF6B00, #FF8C35)' }}>
              <Icon name="Zap" size={16} color="#fff" />
            </div>
            <span className="text-lg font-bold tracking-tight" style={{ fontFamily: "'Inter Tight', sans-serif", color: '#E8EAED' }}>FormForge</span>
          </div>

          <div className="hidden md:flex items-center gap-8">
            {[
              { label: 'How it works', id: 'how-section' },
              { label: 'Features', id: 'features-section' },
              { label: 'Testimonials', id: 'testimonials-section' },
            ]?.map(({ label, id }) => (
              <button
                key={id}
                onClick={() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })}
                className="text-sm transition-colors"
                style={{ color: '#6B7280' }}
                onMouseEnter={e => e.target.style.color = '#E8EAED'}
                onMouseLeave={e => e.target.style.color = '#6B7280'}
              >
                {label}
              </button>
            ))}
            <button
              onClick={() => navigate('/pricing')}
              className="text-sm transition-colors"
              style={{ color: '#6B7280' }}
              onMouseEnter={e => e.target.style.color = '#E8EAED'}
              onMouseLeave={e => e.target.style.color = '#6B7280'}
            >
              Pricing
            </button>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/login')}
              className="text-sm px-4 py-2 rounded-lg transition-all"
              style={{ color: '#6B7280', border: '1px solid rgba(255,255,255,0.08)' }}
              onMouseEnter={e => { e.currentTarget.style.color = '#E8EAED'; e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = '#6B7280'; e.currentTarget.style.background = 'transparent'; }}
            >
              Log In
            </button>
            <button
              onClick={() => navigate('/register')}
              className="text-sm px-4 py-2 rounded-lg text-white font-medium transition-all hover:scale-105"
              style={{ background: 'linear-gradient(135deg, #FF6B00, #FF8C35)', boxShadow: '0 0 16px rgba(255,107,0,0.3)' }}
            >
              Start Free
            </button>
          </div>
        </div>
      </nav>
      {/* ── HERO ── */}
      <section className="relative min-h-screen flex items-center justify-center px-6 pt-20">
        {/* Ambient background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{ x: [0, 40, 0], y: [0, -30, 0] }}
            transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute top-1/4 left-1/3 w-[500px] h-[500px] rounded-full opacity-[0.07]"
            style={{ background: 'radial-gradient(circle, #FF6B00 0%, transparent 70%)' }}
          />
          <motion.div
            animate={{ x: [0, -30, 0], y: [0, 30, 0] }}
            transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut', delay: 4 }}
            className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full opacity-[0.06]"
            style={{ background: 'radial-gradient(circle, #00D4FF 0%, transparent 70%)' }}
          />
        </div>

        <div className="relative max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-widest mb-8"
            style={{ border: '1px solid rgba(255,107,0,0.25)', background: 'rgba(255,107,0,0.07)', color: '#FF6B00' }}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
            Built for personal trainers
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, delay: 0.1 }}
            className="text-5xl md:text-[72px] font-bold tracking-tight leading-[1.05] mb-6"
            style={{ fontFamily: "'Inter Tight', sans-serif", color: '#E8EAED' }}
          >
            One platform.<br />
            <span
              className="bg-clip-text text-transparent"
              style={{ backgroundImage: 'linear-gradient(135deg, #FF6B00 0%, #FF8C35 45%, #00D4FF 100%)' }}
            >
              Every client. Every plan.
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, delay: 0.2 }}
            className="text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed"
            style={{ color: '#6B7280' }}
          >
            FormForge gives personal trainers a single place to build workout plans, design nutrition programmes, track client progress, and communicate — without switching between five different tools.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, delay: 0.3 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-14"
          >
            <button
              onClick={() => navigate('/register')}
              className="w-full sm:w-auto px-8 py-4 rounded-xl text-white font-semibold text-base transition-all duration-200 hover:scale-105"
              style={{ background: 'linear-gradient(135deg, #FF6B00, #FF8C35)', boxShadow: '0 0 28px rgba(255,107,0,0.35)' }}
            >
              Start Free — No Card Needed
            </button>
            <button
              onClick={() => document.getElementById('how-section')?.scrollIntoView({ behavior: 'smooth' })}
              className="w-full sm:w-auto px-8 py-4 rounded-xl font-medium text-base transition-all duration-200 flex items-center justify-center gap-2"
              style={{ border: '1px solid rgba(255,255,255,0.1)', color: '#E8EAED', background: 'rgba(255,255,255,0.03)' }}
            >
              See how it works
              <Icon name="ArrowDown" size={16} />
            </button>
          </motion.div>

          {/* Social proof bar */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.55 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-12 pt-8"
            style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
          >
            {[
              { value: '5 min', label: 'to onboard a new client' },
              { value: '100%', label: 'of plans in one place' },
              { value: '0 apps', label: 'to juggle alongside this' },
            ]?.map((s, i) => (
              <div key={i} className="text-center">
                <div className="text-2xl font-bold" style={{ fontFamily: "'Inter Tight', sans-serif", color: '#FF6B00' }}>{s?.value}</div>
                <div className="text-xs mt-0.5" style={{ color: '#6B7280' }}>{s?.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>
      {/* ── HOW IT WORKS ── */}
      <section id="how-section" className="py-28 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: '#00D4FF' }}>How it works</div>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight" style={{ fontFamily: "'Inter Tight', sans-serif", color: '#E8EAED' }}>
              From intake to results,<br />in one workflow.
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 relative">
            {/* Connector line */}
            <div className="hidden md:block absolute top-10 left-[calc(16.67%+1rem)] right-[calc(16.67%+1rem)] h-px" style={{ background: 'linear-gradient(90deg, rgba(255,107,0,0.3), rgba(0,212,255,0.3), rgba(170,255,0,0.3))' }} />

            {steps?.map((step, i) => (
              <motion.div
                key={step?.number}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.12, duration: 0.6 }}
                className="relative rounded-2xl p-7"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
              >
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center mb-5"
                  style={{ background: `${step?.color}15`, border: `1px solid ${step?.color}30` }}
                >
                  <Icon name={step?.icon} size={20} color={step?.color} />
                </div>
                <div className="text-xs font-bold tracking-widest mb-2" style={{ color: step?.color, fontFamily: "'Inter Tight', sans-serif" }}>{step?.number}</div>
                <h3 className="text-lg font-semibold mb-3" style={{ fontFamily: "'Inter Tight', sans-serif", color: '#E8EAED' }}>{step?.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: '#6B7280' }}>{step?.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      {/* ── FEATURES ── */}
      <section id="features-section" className="py-28 px-6" style={{ background: 'rgba(255,255,255,0.01)' }}>
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-14"
          >
            <div className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: '#FF6B00' }}>Features</div>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight max-w-md" style={{ fontFamily: "'Inter Tight', sans-serif", color: '#E8EAED' }}>
                Everything your<br />coaching business needs.
              </h2>
              <p className="text-sm max-w-xs" style={{ color: '#6B7280' }}>
                No integrations. No third-party apps. No spreadsheets. Just one platform that does it all.
              </p>
            </div>
          </motion.div>

          {/* Asymmetric bento grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {features?.map((f, i) => (
              <motion.div
                key={f?.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.07, duration: 0.55 }}
                className={`rounded-2xl p-7 transition-all duration-300 group cursor-default ${f?.size || ''}`}
                style={{
                  background: f?.highlight ? `${f?.accent}08` : 'rgba(255,255,255,0.03)',
                  border: f?.highlight ? `1px solid ${f?.accent}25` : '1px solid rgba(255,255,255,0.07)',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = `${f?.accent}10`;
                  e.currentTarget.style.borderColor = `${f?.accent}40`;
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = f?.highlight ? `${f?.accent}08` : 'rgba(255,255,255,0.03)';
                  e.currentTarget.style.borderColor = f?.highlight ? `${f?.accent}25` : 'rgba(255,255,255,0.07)';
                }}
              >
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center mb-5"
                  style={{ background: `${f?.accent}15` }}
                >
                  <Icon name={f?.icon} size={20} color={f?.accent} />
                </div>
                <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: "'Inter Tight', sans-serif", color: '#E8EAED' }}>{f?.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: '#6B7280' }}>{f?.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      {/* ── TESTIMONIALS ── */}
      <section id="testimonials-section" className="py-28 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: '#AAFF00' }}>Trainers who use it</div>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight" style={{ fontFamily: "'Inter Tight', sans-serif", color: '#E8EAED' }}>
              Real trainers. Real results.
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {testimonials?.map((t, i) => (
              <motion.div
                key={t?.name}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.6 }}
                className="rounded-2xl p-7 flex flex-col gap-5"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
              >
                {/* Metric callout */}
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold" style={{ fontFamily: "'Inter Tight', sans-serif", color: t?.color }}>{t?.metric}</span>
                  <span className="text-xs" style={{ color: '#6B7280' }}>{t?.metricLabel}</span>
                </div>

                <p className="text-sm leading-relaxed flex-1" style={{ color: '#9CA3AF' }}>"{t?.quote}"</p>

                <div className="flex items-center gap-3 pt-2" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                    style={{ background: `${t?.color}20`, color: t?.color, border: `1px solid ${t?.color}30`, fontFamily: "'Inter Tight', sans-serif" }}
                  >
                    {t?.avatar}
                  </div>
                  <div>
                    <div className="text-sm font-semibold" style={{ color: '#E8EAED' }}>{t?.name}</div>
                    <div className="text-xs" style={{ color: '#6B7280' }}>{t?.role}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      {/* ── CTA ── */}
      <section className="py-28 px-6">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative rounded-3xl overflow-hidden p-12 md:p-16 text-center"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,107,0,0.18)' }}
          >
            <div
              className="absolute inset-0 pointer-events-none opacity-10"
              style={{ background: 'radial-gradient(ellipse at 50% 0%, #FF6B00 0%, transparent 60%)' }}
            />
            <div className="relative">
              <div className="text-xs font-semibold uppercase tracking-widest mb-4" style={{ color: '#FF6B00' }}>Get started today</div>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4" style={{ fontFamily: "'Inter Tight', sans-serif", color: '#E8EAED' }}>
                Ready to coach<br />without the chaos?
              </h2>
              <p className="text-base mb-10 max-w-md mx-auto" style={{ color: '#6B7280' }}>
                30-day free trial. No credit card required. Up to 5 clients included. Cancel any time.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <button
                  onClick={() => navigate('/register')}
                  className="w-full sm:w-auto px-8 py-4 rounded-xl text-white font-semibold text-base transition-all duration-200 hover:scale-105"
                  style={{ background: 'linear-gradient(135deg, #FF6B00, #FF8C35)', boxShadow: '0 0 28px rgba(255,107,0,0.35)' }}
                >
                  Create Free Account
                </button>
                <button
                  onClick={() => navigate('/pricing')}
                  className="w-full sm:w-auto px-8 py-4 rounded-xl font-medium text-base transition-all duration-200"
                  style={{ border: '1px solid rgba(255,255,255,0.1)', color: '#E8EAED', background: 'rgba(255,255,255,0.03)' }}
                >
                  View Pricing
                </button>
              </div>
              <p className="text-xs mt-6" style={{ color: '#4B5563' }}>
                Already have an account?{' '}
                <button
                  onClick={() => navigate('/login')}
                  className="transition-colors"
                  style={{ color: '#FF6B00' }}
                >
                  Log in →
                </button>
              </p>
            </div>
          </motion.div>
        </div>
      </section>
      {/* ── FOOTER ── */}
      <footer className="py-10 px-6" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #FF6B00, #FF8C35)' }}>
              <Icon name="Zap" size={12} color="#fff" />
            </div>
            <span className="text-sm font-semibold" style={{ color: '#E8EAED' }}>FormForge</span>
            <span className="text-sm ml-2" style={{ color: '#4B5563' }}>— Built for trainers who take their craft seriously.</span>
          </div>
          <div className="flex items-center gap-6">
            {[
              { label: 'Log In', path: '/login' },
              { label: 'Register', path: '/register' },
              { label: 'Pricing', path: '/pricing' },
            ]?.map(({ label, path }) => (
              <button
                key={path}
                onClick={() => navigate(path)}
                className="text-sm transition-colors"
                style={{ color: '#4B5563' }}
                onMouseEnter={e => e.target.style.color = '#9CA3AF'}
                onMouseLeave={e => e.target.style.color = '#4B5563'}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
